import type { ReactElement } from "react";
import type {
  DashboardSignalPanelProps,
  DashboardSignalRowProps,
} from "@/modules/dashboard/types/dashboard-analytics.types";

/**
 * Shared shell for the three signal panels.
 *
 * Empty is the COMMON case for these panels in production — most stores have
 * no online payment attempts and almost no discount rows — so the empty branch
 * is a first-class state, not a fallback. It explains what the panel will show
 * once data exists rather than rendering a row of zeros, which would read as
 * "your gateway fails 0% of the time" (a claim about nothing) or simply as a
 * broken dashboard.
 */
export function DashboardSignalPanel({
  title,
  subtitle,
  isEmpty,
  emptyTitle,
  emptyBody,
  children,
}: DashboardSignalPanelProps): ReactElement {
  return (
    <section className="flex min-w-0 flex-col rounded-[16px] border border-[color:var(--line2)] bg-[color:var(--card)] p-4">
      <div className="min-w-0">
        <h3 className="text-[14px] font-bold text-[color:var(--text-primary)]">
          {title}
        </h3>
        <p className="mt-0.5 text-[11.5px] leading-[1.45] text-[color:var(--text-muted)]">
          {subtitle}
        </p>
      </div>

      {isEmpty ? (
        <div className="mt-3 flex-1 rounded-[10px] border border-dashed border-[color:var(--border-light)] bg-[color:var(--soft)] px-3 py-4">
          <p className="text-[12.5px] font-semibold text-[color:var(--text-primary)]">
            {emptyTitle}
          </p>
          <p className="mt-1 text-[11.5px] leading-[1.5] text-[color:var(--text-muted)]">
            {emptyBody}
          </p>
        </div>
      ) : (
        <div className="mt-3 flex-1">{children}</div>
      )}
    </section>
  );
}

/** Label + value row shared by the panels. */
export function SignalRow({
  label,
  value,
  meta,
  strong = false,
}: DashboardSignalRowProps): ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--hair)] py-[7px] last:border-b-0">
      <span className="min-w-0 truncate text-[11.5px] text-[color:var(--text-muted)]">
        {label}
      </span>
      <span className="flex shrink-0 items-baseline gap-1.5">
        <span
          className={[
            "font-mono text-[12.5px] tabular-nums",
            strong
              ? "font-bold text-[color:var(--text-primary)]"
              : "text-[color:var(--text-secondary)]",
          ].join(" ")}
        >
          <bdi>{value}</bdi>
        </span>
        {/* Outside the mono face on purpose — see DashboardSignalRowProps. */}
        {meta ? (
          <span className="text-[10.5px] text-[color:var(--text-muted)]">
            <bdi>{meta}</bdi>
          </span>
        ) : null}
      </span>
    </div>
  );
}
